import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';
import { printGiftShopReceipt } from '../utils/print';

const CATEGORIES = [
  { id:'souvenirs', label:'Souvenirs', icon:'🎁' },
  { id:'clothing',  label:'Clothing',  icon:'👕' },
  { id:'art',       label:'Art & Crafts', icon:'🎨' },
  { id:'jewelry',   label:'Jewelry',   icon:'💍' },
  { id:'food_drink',label:'Food & Drinks', icon:'🍫' },
  { id:'books',     label:'Books',     icon:'📚' },
  { id:'toiletries',label:'Toiletries', icon:'🧴' },
  { id:'electronics',label:'Electronics', icon:'📱' },
  { id:'toys',      label:'Toys',      icon:'🧸' },
  { id:'other',     label:'Other',     icon:'📦' },
];
const CAT_ICON  = Object.fromEntries(CATEGORIES.map(c=>[c.id,c.icon]));
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c=>[c.id,c.label]));

const MODE_INFO = {
  cash:         { label:'Cash',        icon:'💵', hint:'No transaction code needed', color:'#2ecc71' },
  mpesa:        { label:'M-Pesa',      icon:'📱', hint:'Enter M-Pesa confirmation code from SMS', color:'#00b300' },
  pdq:          { label:'PDQ / Card',  icon:'💳', hint:'Enter PDQ authorization code', color:'#9b59b6' },
  room_charge:  { label:'Room Charge', icon:'🏨', hint:'Charge to guest folio — confirmed at checkout', color:'#e67e22' },
};

const blankItem = { name:'', sku:'', category:'souvenirs', description:'', selling_price:'', cost_price:'', stock_qty:'0', reorder_level:'5', is_active:true, image:'' };

export default function GiftShopPage({ notify, userRole }) {
  const [tab, setTab]         = useState('pos');
  const [items, setItems]     = useState([]);
  const [sales, setSales]     = useState([]);
  const [pendingSales, setPendingSales] = useState([]);
  const [guests, setGuests]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [catFilter, setCatFilter] = useState('');

  // POS cart
  const [cart, setCart]             = useState([]);
  const [payMode, setPayMode]       = useState('cash');
  const [txCode, setTxCode]         = useState('');
  const [customerName, setCustomerName] = useState('');
  const [linkedGuest, setLinkedGuest]   = useState('');
  const [processing, setProcessing]     = useState(false);
  const [payModal, setPayModal]         = useState(null); // bill-pending sale for modal

  // Item management
  const [itemModal, setItemModal]   = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [itemForm, setItemForm]     = useState(blankItem);
  const [restockModal, setRestockModal] = useState(null);
  const [restockQty, setRestockQty]     = useState('');

  // Sales history
  const [saleDetail, setSaleDetail] = useState(null);

  const canManage = ['admin', 'gift_shop', 'restaurant_manager'].includes(userRole);
  const isCashier = ['cashier', 'admin'].includes(userRole);

  const load = useCallback(async () => {
    const [i, s, sp, sm, g] = await Promise.all([
      api.getGiftShopItems('?active=true'),
      api.getGiftShopSales('?status=paid'),
      api.getGiftShopSales('?status=payment_requested'),
      api.getGiftShopSummary(),
      api.getGuests(),
    ]);
    setItems(i.data.results || i.data);
    setSales(s.data.results || s.data);
    setPendingSales(sp.data.results || sp.data);
    setSummary(sm.data);
    setGuests((g.data.results || g.data).filter(gu=>gu.status==='checked-in'));
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  // ─── Cart ────────────────────────────────────────────────────────────────────
  const addToCart = (item) => {
    if (item.stock_qty <= 0) { notify(`${item.name} is out of stock`, 'error'); return; }
    setCart(c => {
      const ex = c.find(x=>x.id===item.id);
      if (ex) {
        if (ex.qty >= item.stock_qty) { notify('Max stock reached','error'); return c; }
        return c.map(x=>x.id===item.id?{...x,qty:x.qty+1,subtotal:(x.qty+1)*x.unit_price}:x);
      }
      return [...c,{id:item.id,name:item.name,sku:item.sku,unit_price:parseFloat(item.selling_price),qty:1,subtotal:parseFloat(item.selling_price),stock_qty:item.stock_qty}];
    });
  };
  const updQty = (id,qty) => {
    const n = parseInt(qty);
    if (n<=0) { setCart(c=>c.filter(x=>x.id!==id)); return; }
    setCart(c=>c.map(x=>x.id!==id?x:{...x,qty:Math.min(n,x.stock_qty),subtotal:Math.min(n,x.stock_qty)*x.unit_price}));
  };
  const remFromCart = (id) => setCart(c=>c.filter(x=>x.id!==id));
  const cartTotal = cart.reduce((s,x)=>s+x.subtotal,0);

  // ─── Submit sale (waiter-style) ──────────────────────────────────────────────
  const submitSale = async () => {
    if (!cart.length) { notify('Cart is empty','error'); return; }
    const needsCode = ['mpesa','pdq'].includes(payMode);
    if (needsCode && !txCode.trim()) { notify(`Transaction code required for ${payMode}`,'error'); return; }
    setProcessing(true);
    try {
      const payload = {
        items: cart.map(x=>({id:x.id,qty:x.qty})),
        payment_mode: payMode,
        transaction_code: txCode,
        customer_name: customerName,
        guest_id: linkedGuest || null,
      };
      const r = await api.createGiftShopSale(payload);
      const sale = r.data;

      setCart([]); setPayMode('cash'); setTxCode(''); setCustomerName(''); setLinkedGuest('');
      await load();

      if (sale.status === 'paid') {
        // Room charge — direct to folio
        printGiftShopReceipt(sale);
        notify(`GS-${sale.sale_ref} charged to room. Receipt printed.`);
      } else {
        // Waiting cashier confirmation
        notify(`GS-${sale.sale_ref} submitted to cashier for confirmation.`);
      }
    } catch(e) {
      notify(e.response?.data?.error||'Sale error','error');
    } finally { setProcessing(false); }
  };

  // ─── Cashier confirm ─────────────────────────────────────────────────────────
  const confirmGiftPayment = async (saleId, mode, tx) => {
    await api.confirmGiftPayment(saleId, { payment_mode: mode, transaction_code: tx });
    await load();
    const sale = pendingSales.find(s=>s.id===saleId);
    if (sale) printGiftShopReceipt({ ...sale, payment_mode: mode, transaction_code: tx });
    setPayModal(null);
    notify('Gift shop payment confirmed! Receipt printed.');
  };

  // ─── Item management ────────────────────────────────────────────────────────
  const openItemModal = (item=null) => {
    if (item) { setEditItem(item); setItemForm({ name:item.name, sku:item.sku||'', category:item.category, description:item.description||'', selling_price:String(item.selling_price), cost_price:String(item.cost_price||'0'), stock_qty:String(item.stock_qty), reorder_level:String(item.reorder_level||5), is_active:item.is_active, image:item.image||'' }); }
    else { setEditItem(null); setItemForm(blankItem); }
    setItemModal(true);
  };
  const saveItem = async () => {
    if (!itemForm.name || !itemForm.selling_price) { notify('Name and selling price required','error'); return; }
    if (parseFloat(itemForm.selling_price)<=0) { notify('Selling price must be positive','error'); return; }
    try {
      if (editItem) await api.updateGiftShopItem(editItem.id, itemForm);
      else await api.createGiftShopItem(itemForm);
      setItemModal(false);
      await load();
      notify(editItem?'Item updated!':'Item added!');
    } catch(e) { notify(e.response?.data?.name?.[0]||'Error saving item','error'); }
  };
  const deleteItem = async (id) => {
    if (!window.confirm('Remove this item?')) return;
    await api.deleteGiftShopItem(id);
    await load();
    notify('Item removed');
  };
  const doRestock = async () => {
    const qty = parseInt(restockQty);
    if (!qty||qty<=0) { notify('Enter positive quantity','error'); return; }
    await api.restockGiftShopItem(restockModal.id, { qty });
    setRestockModal(null); setRestockQty('');
    await load();
    notify(`Restocked ${qty} units of ${restockModal.name}`);
  };

  const posItems = items.filter(i=>(!catFilter||i.category===catFilter)&&(!searchQ||i.name.toLowerCase().includes(searchQ.toLowerCase())));

  return (
    <div>
      <PageHeader icon="🛍️" title="Gift Shop" sub="Point of sale · Inventory · Sales"/>

      {summary && (
        <StatCards stats={[
          { label:"Today's Revenue", value:fmtKES(summary.today_revenue), color:'#c8a96e', icon:'💰', sub:`${summary.today_txns} transactions` },
          { label:"Today's Sales",   value:summary.today_txns,            color:'#3498db', icon:'🛍️' },
          { label:'Pending Payment', value:summary.pending_payment||0,    color:'#e67e22', icon:'⏳' },
          { label:'Low Stock Items', value:summary.low_stock_count,       color:'#e74c3c', icon:'⚠️', sub: summary.low_stock_count > 0 ? 'Restock needed' : 'Stock OK' },
        ]}/>
      )}

      <TabBar tabs={[
        { id:'pos',       label:'Point of Sale',             icon:'🛒' },
        { id:'cashier',   label:`Payment Queue (${pendingSales.length})`, icon:'💳' },
        { id:'inventory', label:'Inventory',                 icon:'📦' },
        { id:'sales',     label:'Sales History',             icon:'📜' },
      ]} active={tab} onChange={setTab}/>

      {/* ══════════════════════════════════ POS ═══════════════════════════════ */}
      {tab==='pos' && (
        <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1.6fr) minmax(0,1fr)', gap:12 }}>
          {/* Left — Item browser */}
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
              <input style={{ ...inputStyle, flex:1, minWidth:160 }} placeholder="🔍 Search…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
            </div>
            <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
              {[{id:'',label:'All',icon:''},...CATEGORIES].map(c=>(
                <button key={c.id} onClick={()=>setCatFilter(c.id===catFilter&&c.id?'':c.id)}
                  style={{ fontSize:11, padding:'4px 10px', borderRadius:12, fontFamily:'inherit', cursor:'pointer',
                    border:`1px solid ${catFilter===c.id?'#c8a96e':'var(--borderMid)'}`,
                    background:catFilter===c.id?'#c8a96e22':'transparent',
                    color:catFilter===c.id?'#c8a96e':'#666' }}>
                  {c.icon} {c.label||'All'}
                </button>
              ))}
            </div>
            {posItems.length===0 ? <Empty text="No items found"/> : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))', gap:8 }}>
                {posItems.map(item=>{
                  const inCart = cart.find(x=>x.id===item.id);
                  const oos    = item.stock_qty<=0;
                  return (
                    <div key={item.id} onClick={()=>!oos&&addToCart(item)}
                      style={{ background:oos?'#0d0d0d':'var(--bgCard)', border:`1px solid ${inCart?'#c8a96e':oos?'var(--bgInput)':'var(--border)'}`,
                        borderRadius:4, padding:10, cursor:oos?'not-allowed':'pointer', opacity:oos?0.5:1,
                        boxShadow:inCart?'0 0 0 1px #c8a96e44':undefined }}>
                      {/* Product image — actual photo if uploaded, else category emoji */}
                      <div style={{ width:'100%', height:72, borderRadius:3, marginBottom:6, overflow:'hidden',
                        background:'#111', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {item.image
                          ? <img src={item.image} alt={item.name}
                              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                          : <span style={{ fontSize:28 }}>{CAT_ICON[item.category]||'📦'}</span>
                        }
                      </div>
                      <div style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)', marginBottom:2, lineHeight:1.3 }}>{item.name}</div>
                      <div style={{ fontSize:10, color:'var(--textFaint)', marginBottom:5, fontFamily:'monospace' }}>{item.sku}</div>
                      <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e' }}>{fmtKES(item.selling_price)}</div>
                      <div style={{ fontSize:10, color:item.stock_qty<=item.reorder_level?'#e74c3c':'#555', marginTop:3 }}>
                        {oos?'✕ Out of stock':`Stock: ${item.stock_qty}`}
                      </div>
                      {inCart && <div style={{ marginTop:5, padding:'2px 6px', background:'#c8a96e', color:'#111', borderRadius:2, fontSize:10, fontWeight:700, textAlign:'center' }}>✓ ×{inCart.qty}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right — Cart & Payment */}
          <div>
            <Card title={`Cart (${cart.length})`}>
              {cart.length===0 ? (
                <div style={{ textAlign:'center', padding:'30px 0', color:'#444' }}>
                  <div style={{ fontSize:36, marginBottom:8 }}>🛒</div>
                  <div style={{ fontSize:13 }}>Tap items to add to cart</div>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom:10 }}>
                    {cart.map(item=>(
                      <div key={item.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 0', borderBottom:'1px solid #1a1a1a' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:'var(--textPrimary)' }}>{item.name}</div>
                          <div style={{ fontSize:11, color:'var(--textMuted)' }}>{fmtKES(item.unit_price)} each</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:3 }}>
                          <button style={{ ...btnSm, padding:'3px 8px', fontSize:14 }} onClick={()=>updQty(item.id,item.qty-1)}>−</button>
                          <input type="number" min="1" max={item.stock_qty} value={item.qty}
                            onChange={e=>updQty(item.id,e.target.value)}
                            style={{ ...inputStyle, width:42, textAlign:'center', padding:'4px', fontSize:13 }}/>
                          <button style={{ ...btnSm, padding:'3px 8px', fontSize:14 }} onClick={()=>updQty(item.id,item.qty+1)}>+</button>
                        </div>
                        <div style={{ fontWeight:700, color:'#c8a96e', fontSize:13, minWidth:68, textAlign:'right' }}>{fmtKES(item.subtotal)}</div>
                        <button style={{ ...btnSm, padding:'2px 6px', color:'#e74c3c', background:'#1a0a0a' }} onClick={()=>remFromCart(item.id)}>✕</button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderTop:'2px solid #2a2a2a', fontSize:17, fontWeight:700, marginBottom:14 }}>
                    <span style={{ color:'var(--textPrimary)' }}>TOTAL</span>
                    <span style={{ color:'#c8a96e' }}>{fmtKES(cartTotal)}</span>
                  </div>

                  <Field label="Customer Name (optional)">
                    <input style={inputStyle} placeholder="Walk-in / Name" value={customerName} onChange={e=>setCustomerName(e.target.value)}/>
                  </Field>
                  <Field label="Hotel Guest (Room Charge)">
                    <select style={inputStyle} value={linkedGuest} onChange={e=>{setLinkedGuest(e.target.value);if(e.target.value)setPayMode('room_charge');else setPayMode('cash');}}>
                      <option value="">— Walk-in / External —</option>
                      {guests.map(g=><option key={g.id} value={g.id}>{g.name} (Rm {g.room_number})</option>)}
                    </select>
                  </Field>

                  {/* Payment mode selector — waiter style */}
                  <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Payment Method</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:6, marginBottom:12 }}>
                    {Object.entries(MODE_INFO).map(([key,mi])=>(
                      <button key={key} onClick={()=>setPayMode(key)}
                        style={{ background:payMode===key?'var(--border)':'#111', border:`2px solid ${payMode===key?mi.color:'var(--borderMid)'}`,
                          borderRadius:5, padding:'10px 4px', cursor:'pointer', textAlign:'center' }}>
                        <div style={{ fontSize:18, marginBottom:2 }}>{mi.icon}</div>
                        <div style={{ fontSize:8, fontWeight:600, color:payMode===key?mi.color:'var(--textFaint)', letterSpacing:0.5, lineHeight:1.2, textTransform:'uppercase' }}>{mi.label}</div>
                      </button>
                    ))}
                  </div>

                  {/* Hint */}
                  <div style={{ background:'#0e0e0e', border:`1px solid ${MODE_INFO[payMode]?.color||'#333'}33`, borderRadius:3, padding:'7px 10px', marginBottom:10, fontSize:11, color:'var(--textMuted)' }}>
                    ℹ️ {MODE_INFO[payMode]?.hint}
                  </div>

                  {/* Transaction code for mpesa/pdq */}
                  {['mpesa','pdq'].includes(payMode) && (
                    <div style={{ marginBottom:12 }}>
                      <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>
                        {payMode==='mpesa'?'M-Pesa Confirmation Code *':'PDQ Authorization Code *'}
                      </div>
                      <input
                        style={{ ...inputStyle, fontSize:16, letterSpacing:2, textTransform:'uppercase', fontWeight:700, color:MODE_INFO[payMode].color, border:`1px solid ${txCode?MODE_INFO[payMode].color:'var(--borderMid)'}` }}
                        value={txCode} onChange={e=>setTxCode(e.target.value.toUpperCase())} autoFocus
                        placeholder={payMode==='mpesa'?'e.g. QHX7Y3K2AB':'e.g. AUTH123456'}
                      />
                      {!txCode.trim()&&<div style={{ color:'#e74c3c', fontSize:11, marginTop:3 }}>⚠ Code required for {MODE_INFO[payMode].label}</div>}
                    </div>
                  )}

                  {payMode==='room_charge' && (
                    <div style={{ padding:'8px 10px', background:'var(--bgWarning)', border:'1px solid #e67e2233', borderRadius:3, marginBottom:10, fontSize:11, color:'#e67e22' }}>
                      🏨 Will be posted to guest folio — confirmed at checkout
                    </div>
                  )}

                  {(payMode==='cash'||payMode==='room_charge') && (
                    <div style={{ padding:'8px 10px', background:'#0a1a0a', border:'1px solid #2ecc7133', borderRadius:3, marginBottom:10, fontSize:11, color:'#2ecc71' }}>
                      {payMode==='cash'?'💵 Cash — sale will be queued for cashier confirmation':'✓ Room charge posts directly to folio'}
                    </div>
                  )}

                  {/* Payment trigger buttons */}
                  <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                    <button
                      style={{ flex:1, background:'#00b300', border:'none', borderRadius:3, padding:'8px 10px', color:'#fff', fontSize:11, cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}
                      onClick={async () => {
                        const phone = prompt('Enter M-Pesa phone (254XXXXXXXXX):');
                        if (!phone) return;
                        try {
                          const r = await api.mpesaStkPush({ phone, amount: Math.ceil(cartTotal), description: 'Gift Shop' });
                          setTxCode(r.data.CheckoutRequestID || '');
                          setPayMode('mpesa');
                          alert('✅ STK Push sent! Ask customer to enter PIN.');
                        } catch(e) { alert('M-Pesa failed: ' + (e.response?.data?.error || e.message)); }
                      }}
                    >📱 M-Pesa STK</button>
                    <button
                      style={{ flex:1, background:'#635bff', border:'none', borderRadius:3, padding:'8px 10px', color:'#fff', fontSize:11, cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}
                      onClick={async () => {
                        try {
                          const r = await api.stripePayIntent({ amount: Math.ceil(cartTotal * 100), currency: 'kes', description: 'Gift Shop' });
                          setTxCode(r.data.id || '');
                          setPayMode('pdq');
                          alert(`Stripe intent: ${r.data.id}`);
                        } catch(e) { alert('Stripe failed'); }
                      }}
                    >💙 Stripe</button>
                  </div>

                  <button style={{ ...btnPrimary, width:'100%', background:'#c8a96e', color:'#111', fontWeight:700 }}
                    onClick={submitSale} disabled={processing||(['mpesa','pdq'].includes(payMode)&&!txCode.trim())}>
                    {processing?'Submitting…':payMode==='room_charge'?`🏨 Post to Room Folio — ${fmtKES(cartTotal)}`:`💳 Submit to Cashier — ${fmtKES(cartTotal)}`}
                  </button>
                  <button style={{ ...btnSm, width:'100%', marginTop:6, color:'var(--textFaint)' }} onClick={()=>setCart([])}>Clear Cart</button>
                </>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════ CASHIER PAYMENT QUEUE ══════════════════════════════ */}
      {tab==='cashier' && (
        <div>
          {pendingSales.length===0
            ? <Card title="Payment Queue"><Empty text="No pending gift shop payments — queue is clear"/></Card>
            : (
              <div style={{ display:'grid', gap:12 }}>
                {pendingSales.map(sale=>(
                  <GiftShopCashierCard key={sale.id} sale={sale}
                    onConfirm={confirmGiftPayment}
                    canConfirm={isCashier}
                  />
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* ══════════════════════════ INVENTORY ══════════════════════════════════ */}
      {tab==='inventory' && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:10 }}>
            <input style={{ ...inputStyle, flex:1 }} placeholder="🔍 Search inventory…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
            {canManage && <button style={{ ...btnSm, background:'#c8a96e', color:'#111', padding:'7px 16px', fontWeight:700, whiteSpace:'nowrap' }} onClick={()=>openItemModal()}>+ Add Item</button>}
          </div>

          {items.some(i=>i.low_stock) && (
            <div style={{ padding:'10px 14px', background:'#1a0a0a', border:'1px solid #e74c3c44', borderRadius:4, marginBottom:10, fontSize:12 }}>
              <span style={{ color:'#e74c3c', fontWeight:700 }}>⚠ Low Stock: </span>
              <span style={{ color:'var(--textMuted)' }}>{items.filter(i=>i.low_stock).map(i=>i.name).join(', ')}</span>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
            {items.filter(i=>!searchQ||i.name.toLowerCase().includes(searchQ.toLowerCase())).length===0
              ? <div style={{gridColumn:'1/-1'}}><Empty text="No items yet"/></div>
              : items.filter(i=>!searchQ||i.name.toLowerCase().includes(searchQ.toLowerCase())).map(item=>(
              <div key={item.id} style={{ background:'var(--bgCard)', border:`1px solid ${item.low_stock?'#e74c3c44':'var(--border)'}`, borderRadius:4, padding:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      {/* Thumbnail */}
                      <div style={{ width:40, height:40, borderRadius:3, overflow:'hidden', background:'#111', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {item.image
                          ? <img src={item.image} alt={item.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                          : <span style={{ fontSize:18 }}>{CAT_ICON[item.category]||'📦'}</span>
                        }
                      </div>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)' }}>{item.name}</div>
                        <div style={{ fontSize:10, color:'var(--textFaint)', fontFamily:'monospace' }}>{item.sku}</div>
                        <div style={{ fontSize:11, color:'var(--textFaint)' }}>{CAT_LABEL[item.category]||item.category}</div>
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <div style={{ display:'flex', gap:4 }}>
                      <button style={{ ...btnSm, fontSize:11 }} onClick={()=>openItemModal(item)}>Edit</button>
                      <button style={{ ...btnSm, fontSize:11, color:'#e74c3c', background:'#1a0a0a' }} onClick={()=>deleteItem(item.id)}>Del</button>
                    </div>
                  )}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:6, marginBottom:8 }}>
                  <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'7px 10px' }}>
                    <div style={{ fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>Selling Price</div>
                    <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e' }}>{fmtKES(item.selling_price)}</div>
                  </div>
                  <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'7px 10px' }}>
                    <div style={{ fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>Margin</div>
                    <div style={{ fontSize:15, fontWeight:700, color:'#2ecc71' }}>{item.margin_pct!==null?`${item.margin_pct}%`:'—'}</div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderTop:'1px solid #1a1a1a' }}>
                  <div>
                    <span style={{ fontSize:12, color:item.low_stock?'#e74c3c':'#aaa', fontWeight:item.low_stock?700:400 }}>
                      {item.low_stock?'⚠ ':''}Stock: {item.stock_qty}
                    </span>
                    <span style={{ fontSize:11, color:'var(--textFaint)', marginLeft:8 }}>Reorder: {item.reorder_level}</span>
                  </div>
                  {canManage && (
                    <button className="btn btn-default btn-sm"
                      onClick={()=>{setRestockModal(item);setRestockQty('');}}>+ Restock</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ══════════════════════════ SALES HISTORY ══════════════════════════════ */}
      {tab==='sales' && (
        <Card title="Completed Sales">
          {sales.length===0?<Empty text="No sales yet"/>:(
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>{['Sale Ref','Date','Customer','Items','Total','Payment','Status',''].map(h=>(
                    <th key={h} style={{ background:'#0e0e0e', padding:'7px 9px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {sales.map(s=>(
                    <tr key={s.id} style={{ borderBottom:'1px solid #1a1a1a' }}>
                      <td style={{ padding:'8px 9px', color:'#c8a96e', fontWeight:700, fontFamily:'monospace' }}>GS-{s.sale_ref}</td>
                      <td style={{ padding:'8px 9px', color:'var(--textMuted)', whiteSpace:'nowrap' }}>{new Date(s.created_at).toLocaleString('en-KE')}</td>
                      <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{s.guest_name_str||'Walk-in'}</td>
                      <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{(s.items||[]).length} item{s.items?.length!==1?'s':''}</td>
                      <td style={{ padding:'8px 9px', fontWeight:700, color:'#c8a96e' }}>{fmtKES(s.total)}</td>
                      <td style={{ padding:'8px 9px', color:'var(--textMuted)', textTransform:'uppercase', fontSize:10 }}>{(s.payment_mode||'').replace('_',' ')}</td>
                      <td style={{ padding:'8px 9px' }}>
                        <span style={{ padding:'2px 8px', borderRadius:3, fontSize:10, background:s.status==='paid'?'#2ecc7122':'#e67e2222', color:s.status==='paid'?'#2ecc71':'#e67e22', fontWeight:700, textTransform:'uppercase' }}>{s.status}</span>
                      </td>
                      <td style={{ padding:'8px 9px' }}>
                        <button style={{ ...btnSm, fontSize:11 }} onClick={()=>setSaleDetail(s)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ══════════════════════════ ITEM MODAL ═════════════════════════════════ */}
      {itemModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:540, width:'100%', overflowX:'hidden', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e', marginBottom:16 }}>{editItem?'Edit Item':'Add New Item'}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Item Name *"><input style={inputStyle} value={itemForm.name} onChange={e=>setItemForm(f=>({...f,name:e.target.value}))}/></Field>
              </div>
              <Field label="Category">
                <select style={inputStyle} value={itemForm.category} onChange={e=>setItemForm(f=>({...f,category:e.target.value}))}>
                  {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
              </Field>
              <Field label="SKU (auto if blank)">
                <input style={inputStyle} placeholder="e.g. SOU00123" value={itemForm.sku} onChange={e=>setItemForm(f=>({...f,sku:e.target.value}))}/>
              </Field>
              {/* Typed price fields */}
              <Field label="Selling Price (KES) *">
                <input type="number" min="0" step="1" style={{ ...inputStyle, fontSize:16, fontWeight:700, color:'#c8a96e' }}
                  placeholder="e.g. 2500" value={itemForm.selling_price} onChange={e=>setItemForm(f=>({...f,selling_price:e.target.value}))}/>
                {itemForm.selling_price && <div style={{ fontSize:11, color:'#c8a96e', marginTop:3 }}>= {fmtKES(parseFloat(itemForm.selling_price)||0)}</div>}
              </Field>
              <Field label="Cost Price (KES)">
                <input type="number" min="0" step="1" style={{ ...inputStyle, fontSize:16, color:'var(--textMuted)' }}
                  placeholder="e.g. 1200" value={itemForm.cost_price} onChange={e=>setItemForm(f=>({...f,cost_price:e.target.value}))}/>
                {itemForm.selling_price && itemForm.cost_price && parseFloat(itemForm.selling_price)>0 && (
                  <div style={{ fontSize:11, color:'#2ecc71', marginTop:3 }}>
                    Margin: {Math.round((1-parseFloat(itemForm.cost_price)/parseFloat(itemForm.selling_price))*100)}%
                  </div>
                )}
              </Field>
              <Field label="Initial Stock Qty">
                <input type="number" min="0" style={inputStyle} value={itemForm.stock_qty} onChange={e=>setItemForm(f=>({...f,stock_qty:e.target.value}))}/>
              </Field>
              <Field label="Reorder Level">
                <input type="number" min="0" style={inputStyle} value={itemForm.reorder_level} onChange={e=>setItemForm(f=>({...f,reorder_level:e.target.value}))}/>
              </Field>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Description"><textarea style={{ ...inputStyle, minHeight:55, resize:'vertical' }} value={itemForm.description} onChange={e=>setItemForm(f=>({...f,description:e.target.value}))}/></Field>
              </div>
              {/* ── Product Image Upload ── */}
              <div style={{ gridColumn:'1/-1' }}>
                <div style={{ fontSize:11, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>Product Image (optional)</div>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  {/* Preview */}
                  <div style={{ width:64, height:64, borderRadius:4, overflow:'hidden', background:'#111', border:'1px solid #2a2a2a', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {itemForm.image
                      ? <img src={itemForm.image} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                      : <span style={{ fontSize:26 }}>{CAT_ICON[itemForm.category]||'📦'}</span>
                    }
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={{ display:'inline-block', padding:'7px 14px', background:'#1a1a1a', border:'1px dashed #444', borderRadius:4, cursor:'pointer', fontSize:12, color:'var(--textMuted)' }}>
                      📷 {itemForm.image ? 'Change Photo' : 'Upload Photo'}
                      <input type="file" accept="image/*" style={{ display:'none' }}
                        onChange={e => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 2 * 1024 * 1024) { notify('Image must be under 2 MB', 'error'); return; }
                          const reader = new FileReader();
                          reader.onload = ev => setItemForm(f => ({ ...f, image: ev.target.result }));
                          reader.readAsDataURL(file);
                        }}
                      />
                    </label>
                    {itemForm.image && (
                      <button style={{ ...btnSm, marginLeft:8, fontSize:11, color:'#e74c3c', background:'transparent' }}
                        onClick={() => setItemForm(f => ({ ...f, image:'' }))}>✕ Remove</button>
                    )}
                    <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:4 }}>JPG / PNG / WEBP · max 2 MB · shown on POS & inventory</div>
                  </div>
                </div>
              </div>
              <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" id="active" checked={itemForm.is_active} onChange={e=>setItemForm(f=>({...f,is_active:e.target.checked}))} style={{ accentColor:'#c8a96e' }}/>
                <label htmlFor="active" style={{ fontSize:13, color:'var(--textMuted)', cursor:'pointer' }}>Item is active / available for sale</label>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1 }} onClick={saveItem}>{editItem?'Update Item':'Save Item'}</button>
              <button style={{ ...btnSm, padding:'11px 20px' }} onClick={()=>setItemModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════ RESTOCK MODAL ═════════════════════════════ */}
      {restockModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:360, width:'100%', overflowX:'hidden' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e', marginBottom:6 }}>📦 Restock</div>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:14 }}>{restockModal.name} — Current: <strong style={{ color:'var(--textPrimary)' }}>{restockModal.stock_qty}</strong></div>
            <Field label="Quantity to Add">
              <input type="number" min="1" style={{ ...inputStyle, fontSize:16 }} placeholder="Enter quantity" value={restockQty} onChange={e=>setRestockQty(e.target.value)} autoFocus/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1 }} onClick={doRestock}>+ Add Stock</button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setRestockModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════ SALE DETAIL MODAL ══════════════════════════════ */}
      {saleDetail && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:440, width:'100%', overflowX:'hidden' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
              <div>
                <div style={{ fontSize:11, color:'#c8a96e', letterSpacing:3, textTransform:'uppercase' }}>Gift Shop Sale</div>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--textPrimary)' }}>GS-{saleDetail.sale_ref}</div>
              </div>
              <button onClick={()=>setSaleDetail(null)} style={{ background:'none', border:'none', color:'var(--textFaint)', fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ marginBottom:12, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'5px 12px' }}>
              {[['Date',new Date(saleDetail.created_at).toLocaleString('en-KE')],
                ['Customer',saleDetail.guest_name_str||saleDetail.customer_name||'Walk-in'],
                ['Served By',saleDetail.served_by_name||'—'],
                ['Payment',(saleDetail.payment_mode||'').toUpperCase().replace('_',' ')],
                ['Status',saleDetail.status],
                ['Confirmed By',saleDetail.confirmed_by_name||'—'],
              ].map(([l,v])=>(
                <div key={l}><div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1 }}>{l}</div>
                <div style={{ fontSize:12, color:'var(--textSecond)' }}>{v}</div></div>
              ))}
            </div>
            <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'10px 12px', marginBottom:12 }}>
              {(saleDetail.items||[]).map((item,i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px solid #1a1a1a', fontSize:12 }}>
                  <span style={{ color:'var(--textMuted)' }}>{item.name} ×{item.qty}</span>
                  <span style={{ color:'#c8a96e', fontWeight:700 }}>{fmtKES(item.subtotal)}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0 0', fontSize:14, fontWeight:700 }}>
                <span style={{ color:'var(--textPrimary)' }}>TOTAL</span>
                <span style={{ color:'#c8a96e' }}>{fmtKES(saleDetail.total)}</span>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button style={{ ...btnSm, flex:1 }} onClick={()=>printGiftShopReceipt(saleDetail)}>🖨 Print Receipt</button>
              <button style={{ ...btnSm, padding:'9px 18px' }} onClick={()=>setSaleDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Cashier Card Component ─────────────────────────────────────────────────────
function GiftShopCashierCard({ sale, onConfirm, canConfirm }) {
  const [overrideMode, setOverrideMode] = useState('');
  const [overrideTx,   setOverrideTx]   = useState('');
  const [loading, setLoading]           = useState(false);

  const finalMode = overrideMode || sale.payment_mode || 'cash';
  const finalTx   = overrideTx   || sale.transaction_code || '';
  const info      = MODE_INFO[finalMode] || MODE_INFO.cash;

  const handle = async () => {
    if (['mpesa','pdq'].includes(finalMode) && !finalTx.trim()) return;
    setLoading(true);
    try { await onConfirm(sale.id, finalMode, finalTx); }
    catch(e) {}
    finally { setLoading(false); }
  };

  return (
    <div style={{ background:'var(--bgCard)', border:'2px solid #c8a96e', borderRadius:6, padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
        <div>
          <div style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:18 }}>GS-{sale.sale_ref}</div>
          <div style={{ fontSize:12, color:'var(--textMuted)', marginTop:2 }}>
            {sale.served_by_name} · {new Date(sale.created_at).toLocaleTimeString('en-KE')}
          </div>
          <div style={{ fontSize:13, color:'var(--textMuted)', marginTop:4 }}>
            Customer: <strong>{sale.guest_name_str || sale.customer_name || 'Walk-in'}</strong>
          </div>
        </div>
        <div style={{ fontSize:28, fontWeight:700, color:'#c8a96e' }}>{fmtKES(sale.total)}</div>
      </div>

      {/* Items */}
      <div style={{ background:'#0e0e0e', borderRadius:4, padding:'10px 12px', marginBottom:14 }}>
        {(sale.items||[]).map((item,i)=>(
          <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px solid #1a1a1a', fontSize:13 }}>
            <span style={{ color:'var(--textSecond)' }}>{item.name} ×{item.qty}</span>
            <span style={{ color:'#c8a96e', fontWeight:600 }}>{fmtKES(item.subtotal)}</span>
          </div>
        ))}
        <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, borderTop:'1px solid var(--border)', fontWeight:700 }}>
          <span style={{ color:'var(--textPrimary)' }}>TOTAL</span>
          <span style={{ color:'#c8a96e', fontSize:18 }}>{fmtKES(sale.total)}</span>
        </div>
      </div>

      {/* Submitted payment info */}
      {sale.payment_mode && (
        <div style={{ background:'#0a1a0a', border:`1px solid ${info.color}44`, borderRadius:4, padding:'10px 14px', marginBottom:14 }}>
          <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Payment Submitted by Shop</div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:22 }}>{info.icon}</span>
            <div>
              <div style={{ color:info.color, fontWeight:700, textTransform:'uppercase', fontSize:14 }}>{sale.payment_mode.replace('_',' ')}</div>
              {sale.transaction_code
                ? <div style={{ fontFamily:'monospace', fontSize:16, letterSpacing:2, color:'#2ecc71', fontWeight:700 }}>{sale.transaction_code}</div>
                : <div style={{ color:'var(--textFaint)', fontSize:12 }}>No transaction code (cash)</div>}
            </div>
          </div>
        </div>
      )}

      {/* Override */}
      {canConfirm && (
        <>
          <details style={{ marginBottom:12 }}>
            <summary style={{ fontSize:11, color:'var(--textFaint)', cursor:'pointer', letterSpacing:1, textTransform:'uppercase', padding:'4px 0' }}>Override payment details (optional)</summary>
            <div style={{ paddingTop:8, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:8 }}>
              <Field label="Override Mode">
                <select style={inputStyle} value={overrideMode} onChange={e=>setOverrideMode(e.target.value)}>
                  <option value="">Use shop's mode</option>
                  <option value="cash">Cash</option>
                  <option value="mpesa">M-Pesa</option>
                  <option value="pdq">PDQ / Card</option>
                  <option value="complimentary">🎁 Complimentary</option>
                </select>
              </Field>
              <Field label="Override Tx Code">
                <input style={{ ...inputStyle, letterSpacing:1, textTransform:'uppercase' }}
                  value={overrideTx} onChange={e=>setOverrideTx(e.target.value.toUpperCase())} placeholder="Override code…"/>
              </Field>
            </div>
          </details>
          <button style={{ ...btnPrimary, background:'#1a6b3a', fontSize:15, width:'100%' }}
            onClick={handle} disabled={loading||(['mpesa','pdq'].includes(finalMode)&&!finalTx.trim())}>
            {loading?'Confirming…':'✓ Confirm & Mark as Paid'}
          </button>
        </>
      )}
      {!canConfirm && (
        <div style={{ padding:'10px 14px', background:'#1a1a0a', border:'1px solid #c8a96e33', borderRadius:4, fontSize:12, color:'#c8a96e' }}>
          ⏳ Awaiting cashier confirmation…
        </div>
      )}
    </div>
  );
}
